'use client';

import { getSubsByCity } from '@/app/actions/adminActions';
import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function SubsByCity() {
  const [subsByCity, setSubsByCity] = useState<any[]>([]);

  useEffect(() => {
    const fetchSubsByCity = async () => {
      const data = await getSubsByCity();
      console.log(data);
      setSubsByCity(data);
    };
    fetchSubsByCity();
  }, []);

  // Define city groups - small towns mapped to their main cities
  const cityGroups: { [key: string]: string } = {
    KAUNIAINEN: 'ESPOO',
    KAARINA: 'TURKU',
    RAISIO: 'TURKU',
    KANGASALA: 'TAMPERE',
    PIRKKALA: 'TAMPERE'
  };

  // Transform the data into the format required by Recharts
  const transformData = () => {
    // Group data by date
    const groupedByDate: { [key: string]: { [city: string]: number } } = {};

    // Get all unique dates and sort them
    const uniqueDates = Array.from(
      new Set(subsByCity.map((item) => item.starting_date))
    ).sort();

    // Remove the last week's data (most recent date)
    const filteredData = subsByCity.filter(
      (item) => item.starting_date !== uniqueDates[uniqueDates.length - 1]
    );

    // First, normalize city names and group small towns
    const normalizedData = filteredData.map((item) => {
      const originalCity = item.city.toUpperCase();
      // Map small towns to their main cities, or keep the original city name
      const normalizedCity = cityGroups[originalCity] || originalCity;
      return {
        ...item,
        city: normalizedCity
      };
    });

    // Group data by date and normalized city, summing subscriber counts
    normalizedData.forEach((item) => {
      const date = item.starting_date;
      const city = item.city;
      const count = item.subscriber_count;

      if (!groupedByDate[date]) {
        groupedByDate[date] = {};
      }

      // Add to existing count or set initial count
      if (groupedByDate[date][city]) {
        groupedByDate[date][city] += count;
      } else {
        groupedByDate[date][city] = count;
      }
    });

    // Create array of dates sorted chronologically
    const sortedDates = Object.keys(groupedByDate).sort();

    // Create the final data structure
    const chartData = sortedDates.map((date) => {
      const dataPoint: any = { date };
      Object.keys(groupedByDate[date]).forEach((city) => {
        dataPoint[city] = groupedByDate[date][city];
      });
      return dataPoint;
    });

    return chartData;
  };

  // Get unique cities for creating lines (after grouping)
  const getCities = () => {
    const cities = new Set<string>();

    // Get all unique dates and sort them
    const uniqueDates = Array.from(
      new Set(subsByCity.map((item) => item.starting_date))
    ).sort();

    // Remove the last week's data (most recent date)
    const filteredData = subsByCity.filter(
      (item) => item.starting_date !== uniqueDates[uniqueDates.length - 1]
    );

    // Normalize city names and collect unique cities
    filteredData.forEach((item) => {
      const originalCity = item.city.toUpperCase();
      const normalizedCity = cityGroups[originalCity] || originalCity;
      cities.add(normalizedCity);
    });

    return Array.from(cities);
  };

  // Generate random colors for each city line
  const getColor = (index: number) => {
    const colors = [
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff7300',
      '#00ff00',
      '#ff00ff',
      '#00ffff',
      '#ff0000',
      '#0000ff',
      '#ffff00'
    ];
    return colors[index % colors.length];
  };

  const chartData = transformData();
  const cities = getCities();

  // Get the second to last data point date and total subs
  const getLastDataInfo = () => {
    if (chartData.length < 2)
      return { date: '', formattedDate: '', totalSubs: 0 };

    const lastDataPoint = chartData[chartData.length - 1];
    const date = lastDataPoint.date;

    // Format date in Finnish/British format (DD/MM/YYYY)
    const formattedDate = new Date(date).toLocaleDateString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Calculate total subscriptions for that date
    let totalSubs = 0;
    Object.keys(lastDataPoint).forEach((key) => {
      if (key !== 'date') {
        totalSubs += lastDataPoint[key] || 0;
      }
    });

    return { date, formattedDate, totalSubs };
  };

  const { formattedDate: lastFormattedDate, totalSubs: lastTotalSubs } =
    getLastDataInfo();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce(
        (sum: number, entry: any) => sum + (entry.value || 0),
        0
      );

      const date = new Date(label);
      const formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      return (
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'transparent',
            color: '#fff',
            borderRadius: '1.5rem',
            backdropFilter: 'blur(16px)',
            padding: '12px 24px'
          }}
        >
          <p className="m-0 text-sm text-white/50 uppercase">{formattedDate}</p>
          <p className="mb-4 text-lg font-medium">Total: {total} subs</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <span style={{ color: entry.color }}>
                {entry.name}: {entry.value} subs
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex justify-center pt-24">
      <div className="w-full md:w-[1100px]">
        <div className="text-center mb-6">
          <p>
            Total Subs:
            <br />
            <span className="opacity-50">{lastFormattedDate}</span>
          </p>
          <h1 className="text-[120px] leading-none font-thin">
            {lastTotalSubs}
          </h1>
        </div>
        <h1 className="text-lg w-full ml-6 mb-6 text-left text-white">
          Subscriptions by City
        </h1>
        <div className="bg-black p-4 rounded-lg shadow-lg">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#fff' }}
                tickFormatter={(value) => {
                  // Format date to show only month/day
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  });
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#fff' }}
                label={{
                  value: 'Subscribers',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -5,
                  fill: '#fff'
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: '#fff' }} />
              {cities.map((city, index) => (
                <Line
                  key={city}
                  type="monotone"
                  dataKey={city}
                  stroke={getColor(index)}
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
